import { BadgeRoomHeader } from '@/components/BadgeRoomHeader'
import type { ButtonProps } from '@/components/base/button'
import { Button } from '@/components/base/button'
import type { IconTagName } from '@/components/common/icons/Icon'
import { Icon } from '@/components/common/icons/Icon'
import { Feature } from '@/components/Feature'
import { TEST_IDS } from '@/helpers/constants'
import type { RoomPanelType } from '@/helpers/panelMap'
import { getParentPanelType } from '@/helpers/panelMap'
import { cn } from '@/helpers/utils'
import { FEATURES } from '@/stores/features'
import { useResourcesStoreActions } from '@/stores/resources'
import { useRoomStore } from '@/stores/room'
import { useUiStore, useUiStoreActions } from '@/stores/ui'
import { useMemo, type PropsWithChildren } from 'react'

export const RoomHeader = () => {
  const isAudioOnly = useRoomStore(state => state.isAudioOnly)
  const roomPanelOpen = useUiStore(state => state.roomPanelOpen)
  const roomPanelType = useUiStore(state => state.roomPanelType)
  const callState = useRoomStore(state => state.callState)
  const membersMap = useRoomStore(state => state.membersMap)

  const participantCount = useMemo(() => {
    return membersMap.size
  }, [membersMap])

  const { closeRoomPanel, openRoomPanel, setRoomPanelType } =
    useUiStoreActions()

  const currentParentPanel = roomPanelType && getParentPanelType(roomPanelType)
  const callJoined = callState === 'joined'

  const toggleRoomPanel = (panelType: RoomPanelType) => {
    const isSamePanel = currentParentPanel === panelType

    if (roomPanelOpen && isSamePanel) {
      closeRoomPanel()
    } else {
      openRoomPanel()
      setRoomPanelType(panelType)
    }
  }

  return (
    <div
      className="flex w-full items-center justify-between gap-x-8 bg-primary px-5 py-1 md:px-9"
      data-testid={TEST_IDS.ROOM_HEADER}
    >
      <div
        className="flex min-w-0 items-center gap-x-1 transition-all ease-linear md:gap-x-2"
        data-testid={TEST_IDS.ROOM_NAME}
      >
        <RoomDisplayName />
        <RoomLock />
      </div>
      <ul
        className="inline-flex gap-x-1 md:gap-x-2"
        data-testid={TEST_IDS.ROOM_HEADER_CONTROLS}
      >
        {!isAudioOnly && (
          <>
            <Feature name={FEATURES.ROOM_PARTICIPANTS}>
              <li className="relative">
                <HeaderButton
                  disabled={!callJoined}
                  isActive={
                    roomPanelOpen && currentParentPanel === 'participants'
                  }
                  onClick={() => toggleRoomPanel('participants')}
                  tag="person-pin"
                >
                  Participants
                </HeaderButton>
                <BadgeRoomHeader
                  isVisible={
                    callJoined && currentParentPanel !== 'participants'
                  }
                >
                  {participantCount}
                </BadgeRoomHeader>
              </li>
            </Feature>
            <Feature name={FEATURES.ROOM_CHAT}>
              <HeaderButton
                disabled={!callJoined}
                isActive={roomPanelOpen && currentParentPanel === 'chat'}
                onClick={() => toggleRoomPanel('chat')}
                tag="chat"
              >
                Chat
              </HeaderButton>
            </Feature>
          </>
        )}
        <HeaderButton
          disabled={!callJoined}
          isActive={roomPanelOpen && currentParentPanel === 'options'}
          onClick={() => toggleRoomPanel('options')}
          tag="settings"
        >
          Settings
        </HeaderButton>
      </ul>
    </div>
  )
}

type HeaderButtonProps = Omit<ButtonProps, 'className'> &
  PropsWithChildren<{
    isActive?: boolean
    tag: IconTagName
  }>

const HeaderButton = ({
  children,
  isActive,
  tag,
  ...props
}: HeaderButtonProps) => {
  // CSS delay adds hover intent
  return (
    <Button
      className="group m-0 flex h-auto flex-col rounded-none px-0 py-1 text-xs font-semibold text-neutral-light"
      {...props}
    >
      <div
        className={cn(
          'rounded-full p-1 transition-colors',
          isActive
            ? 'gradient p-[8px]'
            : 'border-2 border-transparent p-[6px] delay-button group-hover:border-negative-light group-hover:bg-background',
        )}
      >
        <Icon
          className={cn(
            !isActive && 'delay-button group-hover:fill-negative-light',
          )}
          tag={tag}
          size="xl"
        />
      </div>
      <span className="sr-only">{children}</span>
    </Button>
  )
}

const RoomDisplayName = () => {
  const roomName = useRoomStore(state => state.roomName)
  const { getResourceByName } = useResourcesStoreActions()
  const roomResource = getResourceByName(roomName)

  if (!roomResource) {
    return null
  }

  const roomLabel =
    roomResource.displayName || roomResource.name || roomResource.id

  return (
    <h2 className="truncate text-2xl font-semibold text-neutral-light">
      {roomLabel}
    </h2>
  )
}

const RoomLock = () => {
  const roomLocked = useRoomStore(state => state.roomLocked)

  return (
    <Icon
      size="lg"
      tag="lock"
      variant="neutral"
      className={cn('flex-shrink-0 duration-300', {
        'opacity-0 fade-out-0': !roomLocked,
        'opacity-100 fade-in-100': roomLocked,
      })}
      aria-label={roomLocked ? 'Room locked' : 'Room unlocked'}
      aria-hidden={false}
    />
  )
}
